import { useEffect, useRef, useCallback } from 'react';
import { KeycloakService } from '../services/keycloakService';

interface UseUserActivityOptions {
  activityCheckInterval?: number;
  onActivity?: () => void;
  onActivityError?: () => void;
}

export const useUserActivity = ({
  activityCheckInterval = 60 * 1000, // 1 minute default
  onActivity,
  onActivityError
}: UseUserActivityOptions = {}) => {
  const lastActivityRef = useRef(Date.now());
  const activityTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleUserActivity = useCallback(() => {
    const now = Date.now();

    // Sprawdzamy czy minęło co najmniej 1 minutę od ostatniej aktywności
    if (now - lastActivityRef.current > activityCheckInterval) {
      console.log('Wykryto aktywność użytkownika po okresie bezczynności, odświeżanie tokenu...');
      if (KeycloakService.isLoggedIn()) {
        KeycloakService.updateToken(30)
          .then((refreshed) => {
            if (refreshed) {
              console.log('Token został odświeżony po aktywności użytkownika');
              onActivity?.();
            }
          })
          .catch((error) => {
            console.error('Błąd odświeżania tokenu po aktywności użytkownika:', error);
            onActivityError?.();
          });
      }
    }
    lastActivityRef.current = now;

    // Usuwamy poprzedni timeout i ustawiamy nowy
    if (activityTimeoutRef.current) {
      clearTimeout(activityTimeoutRef.current);
    }
    activityTimeoutRef.current = setTimeout(() => {
      if (KeycloakService.isLoggedIn()) {
        KeycloakService.updateToken(30).catch((error) => {
          console.error('Błąd automatycznego odświeżania tokenu:', error);
          onActivityError?.();
        });
      }
    }, activityCheckInterval);
  }, [activityCheckInterval, onActivity, onActivityError]);

  const handleVisibilityChange = useCallback(() => {
    if (document.visibilityState === 'visible') {
      console.log('Aplikacja stała się widoczna, odświeżanie tokenu...');

      // Przy powrocie do aplikacji próbujemy od razu odświeżyć token
      if (KeycloakService.isLoggedIn()) {
        KeycloakService.updateToken(30)
          .then((refreshed) => {
            if (refreshed) {
              console.log('Token został odświeżony po powrocie do aplikacji');
              onActivity?.();
            }
          })
          .catch((error) => {
            console.error('Błąd odświeżania tokenu po powrocie do aplikacji:', error);
            onActivityError?.();
          });
      }
      // Resetujemy czas ostatniej aktywności
      lastActivityRef.current = Date.now();
    } else {
      console.log('Aplikacja przestała być widoczna');
    }
  }, [onActivity, onActivityError]);

  useEffect(() => {
    // Dodajemy nasłuchiwacze zdarzeń do wykrywania aktywności użytkownika
    const events = ['mousedown', 'keydown', 'touchstart', 'scroll'];
    
    events.forEach(event => {
      window.addEventListener(event, handleUserActivity);
    });
    
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      // Cleanup event listeners
      events.forEach(event => {
        window.removeEventListener(event, handleUserActivity);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      
      // Cleanup timeout
      if (activityTimeoutRef.current) {
        clearTimeout(activityTimeoutRef.current);
      }
    };
  }, [handleUserActivity, handleVisibilityChange]);

  return {
    getLastActivity: () => lastActivityRef.current,
    clearActivityTimeout: () => {
      if (activityTimeoutRef.current) {
        clearTimeout(activityTimeoutRef.current);
        activityTimeoutRef.current = null;
      }
    }
  };
};